/**
 * The entry point.
 *
 * `configureApp` comes first, before anything renders. The shared package
 * throws if it is read unconfigured — deliberately, because a default would let
 * a component render with the wrong product name or the wrong served kind and
 * look like it worked.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import {
  configureApp,
  DeploymentBanner,
  installSessionHandlers,
  RealtimeProvider,
  ScrollArea,
  ThemeProvider,
  Toaster,
} from '@connect/web-shared';

// The product version, not the commit: a client reads the footer, and a hash
// there is an internal identifier they can do nothing with. A named import, so
// the bundle carries this one field rather than the whole manifest.
import { version } from '../package.json';
import { App } from './app/App';
import { REALTIME_TABLE } from './app/realtime';
import './styles.css';

configureApp({
  flavor: 'admin',
  appName: 'Connect Admin',
  // Staff curate the layouts of dozens of registers: they get the control.
  layoutSaving: 'control',
  // The same icon as the client app and the Android app: one product, one mark.
  // Written into `public/` by scripts/make-brand-icons.py.
  logoSrc: '/app-icon.png',
  // This build signs in employees and refuses client users. The server
  // re-decides it at sign-in, at refresh and on every request; this is the UX
  // half of that.
  servedKind: 'employee',
  version,
});

// Wires the API client's 401 handler to the session store. Done here rather
// than inside the client so the client does not depend on the store.
installSessionHandlers();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Authority is re-resolved server-side on every call, so a stale read is
      // never an authorization problem — only a slightly old number. Thirty
      // seconds keeps a register responsive without hammering the API while
      // somebody pages through it.
      staleTime: 30_000,
      // Refetching on focus turns every alt-tab into a burst of requests. A
      // register has an explicit Refresh button instead, which is honest about
      // when the data was read.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry a refusal. A 403 retried three times is three audit
        // entries and the same answer; only a transport failure is worth
        // trying again.
        const status = (error as { status?: number }).status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // A mutation is a user's deliberate action. Retrying one automatically
      // risks doing it twice, which for a create means two records.
      retry: false,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('No #root element. Check index.html.');

createRoot(root).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {/* Live updates while somebody is signed in: see ./app/realtime.ts. */}
          <RealtimeProvider table={REALTIME_TABLE}>
            {/* Everything scrolls in SimpleBar, never the document (tokens.css):
                the shells fill it exactly; pages outside them — signing in, a
                policy — scroll here. */}
            <ScrollArea className="h-dvh">
              <App />
            </ScrollArea>
          </RealtimeProvider>
        </BrowserRouter>
        <Toaster />
        {/*
          Renders nothing unless this build was built as pre-production. Mounted
          here rather than inside the shell so it also appears on the sign-in
          page — which is the one screen somebody sent a link will definitely
          see, and the screen where mistaking this for the live product matters
          most.
        */}
        <DeploymentBanner />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
